import AnimatedEmoji from "@components/AnimatedEmoji"
import FilterlyLogoSvg from "@components/FilterlyLogoSvg"
import GooglePlayAndAppStoreBadgeLinks from "@components/GooglePlayAndAppStoreBadgeLinks"
import Layout from "@components/Layout"
import SubscriptionDialog from "@components/SubscriptionDialog"
import detectPlatform from "@helpers/detectPlatform"
import localizeDate from "@helpers/localizeDate"
import { AnimatePresence, motion } from "framer-motion"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLoaderData } from "react-router-dom"
import { GetSubscriptionLoaderData } from "src/loaders/getSubscriptionLoader"

const stepVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.15 + i * 0.1, duration: 0.35 },
  }),
}

export default function SubscriptionPage() {
  const { revenueCatSubscriptions, userEmail } =
    useLoaderData() as GetSubscriptionLoaderData

  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false)
  const [emailCopied, setEmailCopied] = useState(false)

  const { t } = useTranslation()

  const copyEmail = useCallback(async () => {
    if (!userEmail) return
    await navigator.clipboard.writeText(userEmail)
    setEmailCopied(true)
    setTimeout(() => setEmailCopied(false), 2000)
  }, [userEmail])

  const activeSubscription = revenueCatSubscriptions.find(
    (sub) => !sub.unsubscribe_detected_at,
  )

  const isLifetime =
    activeSubscription &&
    new Date(activeSubscription.expires_date).getFullYear() >= 2099

  const platform = useMemo(() => detectPlatform(), [])
  const isDesktop = platform === "desktop"

  const steps = [
    {
      number: 1,
      title: t("pages.account.subscription.step1Title"),
      description: t(
        isDesktop
          ? "pages.account.subscription.step1DescriptionDesktop"
          : "pages.account.subscription.step1Description",
      ),
      hasStoreBadges: true,
    },
    {
      number: 2,
      title: t("pages.account.subscription.step2Title"),
      description: t("pages.account.subscription.step2Description"),
      showEmail: true,
    },
    {
      number: 3,
      title: t("pages.account.subscription.step3Title"),
      description: t("pages.account.subscription.step3Description"),
    },
  ]

  return (
    <>
      <Layout>
        <div className="flex shrink-0 items-center justify-between">
          <FilterlyLogoSvg />

          {revenueCatSubscriptions.length > 0 && (
            <button
              className="p-4 text-base font-semibold text-white underline hover:opacity-80"
              onClick={() => setOpenSubscriptionDialog(true)}
              type="button"
            >
              {t("pages.account.subscription.subscriptionInfo")}
            </button>
          )}
        </div>

        <div className="flex grow flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full space-y-10"
          >
            <div className="space-y-3">
              <AnimatedEmoji emoji="🎉" />

              <h1 className="heading">
                {t("pages.account.subscription.heading")}
              </h1>

              <p className="text-text-secondary mx-auto max-w-md text-sm leading-relaxed">
                {t("pages.account.subscription.subtitle")}
              </p>
            </div>

            {activeSubscription && (
              <div className="mx-auto max-w-sm rounded-2xl bg-white/5 px-5 py-4">
                <div className="mb-3 flex items-center justify-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                  <span className="text-sm font-semibold text-emerald-400">
                    {t("pages.account.subscription.activeSubscription")}
                  </span>
                </div>

                <div className="flex justify-center gap-8 text-sm">
                  <div>
                    <p className="text-text-secondary">
                      {t("pages.account.subscription.purchasedOn")}
                    </p>
                    <p className="font-semibold text-white">
                      {localizeDate(new Date(activeSubscription.purchase_date))}
                    </p>
                  </div>
                  {!isLifetime && (
                    <div>
                      <p className="text-text-secondary">
                        {t("pages.account.subscription.renewsOn")}
                      </p>
                      <p className="font-semibold text-white">
                        {localizeDate(
                          new Date(activeSubscription.expires_date),
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-6">
              <h2 className="text-lg font-semibold text-white">
                {t("pages.account.subscription.getStartedHeading")}
              </h2>

              <div className="mx-auto max-w-sm space-y-4">
                {steps.map((step) => (
                  <motion.div
                    key={step.number}
                    custom={step.number}
                    initial="hidden"
                    animate="visible"
                    variants={stepVariants}
                    className="rounded-2xl bg-white/5 px-5 py-4 text-left"
                  >
                    <div className="mb-2 flex items-center gap-3">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-bold text-white">
                        {step.number}
                      </span>
                      <h3 className="text-sm font-semibold text-white">
                        {step.title}
                      </h3>
                    </div>

                    <p className="text-text-secondary pl-10 text-sm leading-relaxed">
                      {step.description}
                    </p>

                    {step.showEmail && userEmail && (
                      <button
                        type="button"
                        onClick={copyEmail}
                        className="mt-2 flex w-full cursor-pointer items-center gap-2 pl-10 text-left"
                      >
                        <span className="text-sm font-semibold break-all text-white">
                          {userEmail}
                        </span>
                        <AnimatePresence mode="wait">
                          {emailCopied ? (
                            <motion.span
                              key="copied"
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              className="shrink-0 text-xs font-semibold text-emerald-400"
                            >
                              {t("pages.account.subscription.copied")}
                            </motion.span>
                          ) : (
                            <motion.svg
                              key="icon"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 0.5 }}
                              exit={{ opacity: 0 }}
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              className="h-4 w-4 shrink-0 text-white"
                            >
                              <path d="M7 3.5A1.5 1.5 0 0 1 8.5 2h3.879a1.5 1.5 0 0 1 1.06.44l3.122 3.12A1.5 1.5 0 0 1 17 6.622V12.5a1.5 1.5 0 0 1-1.5 1.5h-1v-3.379a3 3 0 0 0-.879-2.121L10.5 5.379A3 3 0 0 0 8.379 4.5H7v-1Z" />
                              <path d="M4.5 6A1.5 1.5 0 0 0 3 7.5v9A1.5 1.5 0 0 0 4.5 18h7a1.5 1.5 0 0 0 1.5-1.5v-5.879a1.5 1.5 0 0 0-.44-1.06L9.44 6.439A1.5 1.5 0 0 0 8.378 6H4.5Z" />
                            </motion.svg>
                          )}
                        </AnimatePresence>
                      </button>
                    )}

                    {step.hasStoreBadges && (
                      <div className="mt-3">
                        <GooglePlayAndAppStoreBadgeLinks />
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="mx-auto max-w-sm rounded-2xl border border-amber-400/20 bg-amber-400/5 px-5 py-4">
              <p className="text-sm leading-relaxed text-amber-400">
                {t("pages.account.subscription.noNewSubscription")}
              </p>
            </div>

            <div className="space-y-2 pb-2">
              <p className="text-sm font-semibold text-white">
                {t("pages.account.subscription.supportHeading")}
              </p>
              <p className="text-text-secondary text-sm">
                {t("pages.account.subscription.supportDescription")}
              </p>
              <a
                href="mailto:support@filterly.app"
                className="inline-block text-sm font-semibold text-white underline hover:opacity-80"
              >
                support@filterly.app
              </a>
            </div>
          </motion.div>
        </div>
      </Layout>

      {openSubscriptionDialog && revenueCatSubscriptions.length > 0 && (
        <SubscriptionDialog
          onClose={() => setOpenSubscriptionDialog(false)}
          open={openSubscriptionDialog}
          subscriptions={revenueCatSubscriptions}
        />
      )}
    </>
  )
}
